import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { createBrowserHistory } from "history"
import * as R from "ramda"
import { createReduxHistoryContext } from "redux-first-history"
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from "redux-persist"
import storage from "redux-persist/lib/storage" // defaults to localStorage for web
import createSagaMiddleware from "redux-saga"

import rootReducer from "./rootReducer"
import rootSaga from "./rootSaga"
import immutablePersistenceTransform from "./Utils/ImmutablePersistenceTransform"
import { syncIncognitoActive } from "./Utils/incognito"

const PERSISTED_REDUCER_VERSION = 2 // Bump this value to reset persisted reducer (doesn't clear nested persisted reducers)

const persistConfig = {
  key: "chargeback",
  version: PERSISTED_REDUCER_VERSION,
  storage,
  // reducers we don't want stored to persistence.
  // `receiptConfigure` is server-backed and not scoped by business account, so
  // persisting it would restore another account's answers before the
  // authoritative GET resolves. Each slice below owns its own persistReducer.
  blacklist: [
    "router",
    "credits",
    "health",
    "home",
    "requestsVerifiInform",
    "requestsReceipts",
    "receipt",
    "receiptConfigure",
    "receiptBusiness",
    "deflectAnalytics",
    "alertsSlice",
    "paymentMatching",
    "auth",
    "user",
    "nav",
    "setting",
    "errors",
    "shopifyMove",
    "pricing"
  ], // reducers we don't want stored to persistence
  // Optionally, just specify the keys you DO want stored to persistence.
  // An empty array means 'don't store any reducers' -> infinitered/ignite#409
  // whitelist: [],
  transforms: [immutablePersistenceTransform],
  migrate: (state) => {
    if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
      return Promise.resolve(undefined)
    }
    // The blacklist only filters writes: a slice moved out of this bucket would otherwise rehydrate
    // its last legacy copy once, on top of its new shape.
    return Promise.resolve(state && R.omit(persistConfig.blacklist, state))
  }
}

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory()
})

export default async () => {
  const sagaMiddleware = createSagaMiddleware()

  const persistedReducer = persistReducer(
    persistConfig,
    combineReducers({
      ...rootReducer,
      router: routerReducer
    })
  )

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          // temporary, while onSuccess/onError callbacks live in payloads — remove once those containers move to thunks
          ignoredActionPaths: ["payload.onSuccess", "payload.onError"]
        },
        immutableCheck: false // seamless-immutable still backs legacy slices; revisit once they're migrated
      }).concat(sagaMiddleware, routerMiddleware),
    devTools: process.env.NODE_ENV !== "production"
  })

  // Keep the non-React mirror of Incognito mode in sync (chart callbacks read it, see Utils/incognito).
  syncIncognitoActive(store.getState())
  store.subscribe(() => syncIncognitoActive(store.getState()))

  const history = createReduxHistory(store)

  const persistor = await persistStore(store)

  sagaMiddleware.run(rootSaga)

  return {
    store,
    persistor,
    history
  }
}
